/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
// @mui
import { Card, Box, Paper, Button, Radio, Zoom, Tooltip, useTheme, Typography, Divider } from '@mui/material';
// new task system
import LeadTaskList from '../../tasks/LeadTaskList';
import TaskErrorBoundary from '../../../components/TaskErrorBoundary';
import useFeaturePermission from '../../../hooks/useFeaturePermission';
// components
import { Iconify } from '../../../components';
import { NotFound } from '../../../components/empty-content';
import { AssignUserAvatar } from '../../../components/custom-avatar';
import { NormalLoading } from '../../../components/loading';
// hooks
import { useTable } from '../../../hooks';
// redux
import {
    getLeadReminderData,
    followUpSlice,
    updateReminder,
    leadTaskListCounter,
    updateTaskCounter,
    updateLeadTaskCounter,
} from '../../../redux/slices/lead';
import { useSelector, dispatch } from '../../../redux/store';
// utils
import capitalizeFirstLetter from '../../../utils/capitalizedFunc';
import { getMessage } from '../../../utils/getTasksData';
import { getAssignees } from '../../../utils/multiAsignees';
// §9 MUST FE — V1 imports dropped (callApi + reminderFields were only
// used by the legacy /reminders/ direct fetch; that is now deleted).
// style
import { common } from '../../../theme/palette';
import {
    taskCard,
    taskList,
    taskMessage,
    taskUser,
    taskUserAvatar,
    taskReminderWrapper,
    assignUserAvatarStyle
} from '../../../styles/leadTaskReminder';
import { leadListCard, leadTaskPopover } from '../../../styles/ContactLeadPopup';
import { displayFlex } from '../../../styles/chatbotStyles';

const LeadTaskReminder = ({ type, selectedLead, currentTab, isForTab }) => {
    const theme = useTheme();
    const reminderDataId = useRef();
    const isLight = theme.palette.mode === 'light';
    const { canView: hasTasksV2 } = useFeaturePermission('tasks-v2');
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { leadFlowReminder } = useSelector((state) => state.lead);
    const [markComplete, setMarkComplete] = useState(false);
    const [reminderList, setReminderList] = useState([]);
    // §9 MUST FE — completedList state removed alongside the deleted V1 fetch.
    const [additionalTabState, setAdditionalTabState] = useState('All');
    const [loading, setLoading] = useState(true);

    const { page, rowsPerPage } = useTable();

    useEffect(() => {
        if (!leadFlowReminder) {
            setReminderList([]);
            return;
        }

        if (leadFlowReminder?.results) {
            setReminderList(leadFlowReminder?.results);
            setLoading(false);
        }
    }, [leadFlowReminder]);

    const leadId = type === 'LeadDetail' ? selectedLead?.lead_id : '';

    // §9 MUST FE — direct V1 /reminders/ fetch (formerly L84) deleted.
    // V2 practices read completed tasks via <LeadTaskList>; V1 practices fall
    // through to the legacy bottom panel below which still shows pending V1
    // reminders via the redux store.

    useEffect(() => {
        // §9 MUST FE — V2 practices skip the V1 reminder fetch entirely;
        // <LeadTaskList> below handles V2 data. V1 practices keep the legacy
        // store-driven flow so existing UI (legacy bottom panel + ToDoList
        // popups) keeps working.
        if (hasTasksV2) return;
        if (currentTab && practiceId) {
            setAdditionalTabState(currentTab);
            setLoading(true);
            dispatch(getLeadReminderData(practiceId, type, rowsPerPage, 0, 'NEW', leadId, '', '', '', '', currentTab, undefined, true));
        }
    }, [currentTab, practiceId, leadId, hasTasksV2]);

    const handleReminder = (value) => {
        dispatch(updateTaskCounter([]));
        if (value === 'markComplete') {
            if (type === 'LeadDetail' && selectedLead) {
                dispatch(updateLeadTaskCounter([]));
                dispatch(getLeadReminderData(practiceId, type, rowsPerPage, rowsPerPage * page, 'NEW', selectedLead?.lead_id || '', '', '', 'leadNotes', 'counter', additionalTabState, undefined, true));
                dispatch(leadTaskListCounter(practiceId, 'LeadDetail', selectedLead?.status, selectedLead?.lead_id, '', '', undefined, true));
                dispatch(followUpSlice(selectedLead?.lead_id, practiceId, 'LeadDetail'));
            }
        }
    };

    const handleSuccess = (data) => {
        switch (data.message) {
            case 'Task completed !': {
                setMarkComplete(false);
                const key = enqueueSnackbar(data.message || '', {
                    variant: data.success ? 'success' : 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    },
                    autoHideDuration: 5000,
                    action:
                        <>
                            <Button onClick={() => undoReminder()} variant='success' size='small' sx={{ color: common.mintyZest, '&:hover': { bgcolor: 'rgba(84, 214, 44, 0.16)' } }}>
                                Undo
                            </Button>
                            <Iconify onClick={() => closeSnackbar(key)} icon={'radix-icons:cross-2'} height={15} width={15} sx={{ margin: '0px 8px', cursor: 'pointer' }} />
                        </>
                });
                return;
            }
            case 'Task not completed !': {
                setMarkComplete(false);
                enqueueSnackbar(data.message || '', {
                    variant: data.success ? 'success' : 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    },
                });
                return;
            }
            default:
                enqueueSnackbar(data.message || '', {
                    variant: data.success ? 'success' : 'error',
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center'
                    },
                });
                break;
        }
    };

    const undoReminder = () => {
        setMarkComplete(false);
        const data = {
            is_completed: false
        };
        dispatch(updateReminder(data, reminderDataId.current, practiceId, handleReminder, 'markComplete'));
        closeSnackbar();
    };

    const markCompleteBtn = (value) => {
        reminderDataId.current = value;
        setMarkComplete(value);
        const data = {
            // eslint-disable-next-line no-unneeded-ternary
            is_completed: true,
        };
        dispatch(updateReminder(data, value, practiceId, handleReminder, 'markComplete', handleSuccess));
    };

    // ── §9 MUST FE — V2 short-circuit for compact (ToDoList) view.
    //    V2 practices render <LeadTaskList> directly; V1 falls through to the
    //    legacy timeline below.
    if (isForTab && hasTasksV2 && type === 'LeadDetail' && practiceId && leadId) {
        return (
            <Box sx={{ width: '100%' }}>
                <TaskErrorBoundary>
                    <LeadTaskList leadId={leadId} practiceId={practiceId} />
                </TaskErrorBoundary>
            </Box>
        );
    }

    // ── Compact timeline view for Overview tab (V1 only) ──
    if (isForTab) {
        const getTimeColor = (data, completed) => {
            if (completed) return '#919EAB';
            if (data?.datetime_human?.includes('now' || 'NOW')) return '#00A76F';
            return '#FF5630';
        };
        const renderTaskRow = (data, isCompleted) => {
            const assigneeData = data?.assignees?.filter((el) => el?.user?.first_name && !el?.user?.is_superuser);
            return (
                <Box key={data.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%', mb: 1, opacity: isCompleted ? 0.5 : 1 }}>
                    {!isCompleted ? (
                        <Tooltip title={data.id === markComplete ? 'Mark as incomplete' : 'Mark as complete'} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                            <Radio
                                checked={data.id === markComplete}
                                onClick={() => markCompleteBtn(data.id)}
                                name='radioButtons'
                                sx={{ p: 0, mt: '-1px', 'svg': { width: '16px', height: '16px' } }}
                            />
                        </Tooltip>
                    ) : (
                        <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: '#00A76F', width: 16, height: 16, mt: '1px', flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: '12px', fontWeight: isCompleted ? 500 : 600, color: isCompleted ? '#919EAB' : '#212B36', lineHeight: 1.4, textDecoration: isCompleted ? 'line-through' : 'none' }}>
                            {data.title !== '' ? capitalizeFirstLetter(data?.title) : getMessage(data?.lead_treatment?.status, data?.lead?.first_name, data?.lead?.last_name, data?.lead_treatment?.treatment, data?.group, data?.method, data?.note)}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '3px' }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', fontSize: '10px', fontWeight: 600,
                                color: getTimeColor(data, isCompleted),
                            }}>
                                <Iconify icon={'mdi:clock-time-three-outline'} width={10} height={10} sx={{ mr: '3px' }} />
                                {data.datetime_human || ''}
                            </Box>
                            {assigneeData?.length > 0 && (
                                <Box sx={{ display: 'flex', ml: 'auto' }}>
                                    {assigneeData.slice(0, 2).map((assignee, index) => (
                                        <AssignUserAvatar
                                            key={index}
                                            sx={{ height: '18px', width: '18px', fontSize: '0.6rem', border: '1px solid #fff', '&:nth-child(2)': { ml: '-4px' } }}
                                            src={assignee.user?.avatar || ''}
                                            name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                            role={assignee.role}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            );
        };

        const hasPending = reminderList && reminderList.length > 0;

        return (
            <Box sx={{ width: '100%' }}>
                {hasPending && reminderList.map((data) => renderTaskRow(data, false))}
                {!hasPending && (!loading && leadFlowReminder?.count !== undefined) && (
                    <Box sx={{ fontSize: '11px', color: '#C4CDD5' }}>No tasks yet</Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 3 }}>
            {/* ── Tasks V2 — only shown when tasks-v2 feature is enabled for this practice ── */}
            {hasTasksV2 && type === 'LeadDetail' && practiceId && leadId && (
                <Card sx={{ ...taskCard(type), boxShadow: 'none', mb: 2 }}>
                    <Box sx={{ p: '16px' }}>
                        <TaskErrorBoundary>
                            <LeadTaskList leadId={leadId} practiceId={practiceId} />
                        </TaskErrorBoundary>
                    </Box>
                </Card>
            )}

            {/* §9 MUST FE — Legacy reminders rendered only for V1 practices. */}
            {!hasTasksV2 && (
            <>
            {/* ── Legacy Reminders (existing system — do NOT remove until data migration complete) ── */}
            <Box sx={{ mb: 1, px: '4px' }}>
                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Reminders (Legacy)
                </Typography>
                <Divider sx={{ mt: '4px' }} />
            </Box>
            <Card sx={{ ...taskCard(type), boxShadow: 'none' }}>
                <Box sx={{ position: 'relative', m: '16px 16px 0', borderRadius: 2 }}>
                    <NormalLoading tableData={reminderList.length === 0 && 'No Found'} pageCount={leadFlowReminder?.count === undefined && 'No Found'} />
                    <Box sx={{ ...taskList(type), maxHeight: 'none' }}>
                        {reminderList && reminderList.map((data) => {
                            const assigneeData = data?.assignees?.filter((el) => el?.user?.first_name && !el?.user?.is_superuser);
                            return <Box
                                key={data.id}
                                sx={taskReminderWrapper}
                            >
                                <Box sx={{ minWidth: '40px', mt: '24px' }}>
                                    <Tooltip title={data.id === markComplete ? 'Mark as incomplete' : 'Mark as complete'} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                        <Radio
                                            checked={data.id === markComplete}
                                            onClick={() => markCompleteBtn(data.id)}
                                            name='radioButtons'
                                            sx={{ p: '0', 'svg': { width: '24px', height: '24px' } }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Paper sx={{ ...leadListCard(isLight, data?.lead?.is_archived, data?.is_system_generated, data?.lead?.is_archive_pending), cursor: 'default', borderRadius: 0, mt: '24px' }}>
                                    <Box sx={taskMessage}>
                                        <Box>
                                            <Box sx={{ fontSize: '16px' }}>
                                                {data.title !== '' ? <Box sx={{ fontSize: '12px', lineHeight: 'inherit' }} ><b>{capitalizeFirstLetter(data?.title)}</b></Box> : getMessage(data?.lead_treatment?.status, data?.lead?.first_name, data?.lead?.last_name, data?.lead_treatment?.treatment, data?.group, data?.method, data?.note)
                                                }
                                            </Box>
                                            <Box sx={{ ...taskUser, alignItems: 'flex-start', pt: '6px' }}>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    <Box sx={displayFlex} >
                                                        <Box sx={leadTaskPopover(data)}>
                                                            <Iconify icon={'mdi:clock-time-three-outline'} width={12} height={12} sx={{ mr: '5px' }} />
                                                            {data.datetime_human && data.datetime_human || ''}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box sx={{ ...taskUser, alignItems: 'flex-start', pt: '0' }}>
                                            {assigneeData?.length > 0 ?
                                                <Box sx={taskUserAvatar}>
                                                    <Tooltip title={getAssignees(data)} placement='bottom' TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                                                        <Box sx={displayFlex}>
                                                            {assigneeData?.slice(0, 2)?.map((assignee, index) => (
                                                                <AssignUserAvatar
                                                                    key={index}
                                                                    sx={assignUserAvatarStyle}
                                                                    src={assignee.user?.avatar || ''}
                                                                    name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                                                    role={assignee.role}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Tooltip>
                                                </Box> : ''}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>;
                        })}
                        {(reminderList?.length === 0 && leadFlowReminder?.count !== undefined && !loading) && <NotFound />}
                    </Box>
                </Box>
            </Card>
            </>
            )}
        </Box>
    );
};
LeadTaskReminder.propTypes = {
    type: PropTypes.string,
    selectedLead: PropTypes.object,
    currentTab: PropTypes.string,
    isForTab: PropTypes.bool
};

export default LeadTaskReminder;